import { connect, getIn, FormikContextType } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapIcon } from '~/Components/Icons';
import { ISelectOptions } from '~/Models/interfaces/SelectOption.models';
import { IAddress, InputAddress } from '~/UIKit/Form/InputAddress';
import { ExtendedLocation } from '../../ExtendedLocation.model';
import { defaultLatLong } from '~/Constants/Values.constants';

export const LocationEditCustomInputAddress = connect<
  {
    countries: ISelectOptions;
    prefix?: string;
    disabled?: boolean;
    simple?: boolean;
    hideIcon?: boolean;
  },
  ExtendedLocation
>(
  ({
    formik,
    countries,
    prefix,
    disabled,
    simple = false,
    hideIcon = false,
  }: {
    formik: FormikContextType<ExtendedLocation>;
    countries: ISelectOptions;
    prefix?: string;
    disabled?: boolean;
    simple?: boolean;
    hideIcon?: boolean;
  }) => {
    const { t } = useTranslation();
    const [fullAddress, setFullAddress] = useState('');

    const fields = {
      street: prefix ? `${prefix}.street` : 'street',
      zipCode: prefix ? `${prefix}.zipCode` : 'zipCode',
      city: prefix ? `${prefix}.city` : 'city',
      country: prefix ? `${prefix}.country` : 'countryISOA2',
      countryName: prefix ? `${prefix}.countryName` : 'countryName',
    };

    useEffect(() => {
      if (formik?.values) {
        const city = getIn(formik.values, fields.city);
        const zipCode = getIn(formik.values, fields.zipCode);
        const street = getIn(formik.values, fields.street);
        const countryISOA2 =
          getIn(formik.values, fields.country) || getIn(formik.values, fields.countryName);
        const country =
          countryISOA2 &&
          countries.find(
            (country) =>
              country.value === countryISOA2 ||
              country?.label?.toUpperCase() === countryISOA2?.toUpperCase(),
          );

        if ((street && city && country) || disabled) {
          setFullAddress(`${street}, ${zipCode} ${city}, ${country.label}`);
        }
      }
    }, [formik?.values, disabled]);

    const handleLocationSelect = (address: IAddress) => {
      if (!address) {
        return;
      }
      const { city, zipCode, street, countryISOA2, latitude, longitude, countryName } = address;
      const country = countries.find((country) => country.value === countryISOA2);

      formik.setValues((prevValues) => ({
        ...prevValues,
        [fields.street]: street,
        [fields.zipCode]: zipCode || '',
        [fields.city]: city || '',
        [fields.country]: countryISOA2 || '',
        [fields.countryName]: country?.label || countryName || '',
        latitude: latitude || defaultLatLong.lat,
        longitude: longitude || defaultLatLong.lng,
        latitudeMap: latitude || defaultLatLong.lat,
        longitudeMap: longitude || defaultLatLong.lng,
      }));

      setFullAddress(`${street}, ${zipCode} ${city}, ${country?.label}`);
    };

    return (
      <InputAddress
        initialValue={fullAddress}
        onLocationSelect={handleLocationSelect}
        icon={<MapIcon />}
        label={`${t('form.field.location.address')}*`}
        placeholder={t('form.field.location.address')}
        disabled={disabled}
        simple={simple}
        hideIcon={hideIcon}
      />
    );
  },
);
